/**
 * "Scrivi su WhatsApp" dalla scheda offerta: apre la chat del Lead/Cliente
 * dell'offerta con l'hook unico useOpenWhatsAppChat (drawer sul posto, scelta
 * del numero aziendale se ce n'e' piu' d'uno) e, se c'e' un'offerta, mette nel
 * campo di scrittura un testo di sollecito da rivedere. Niente viene inviato.
 *
 * Il testo si deposita solo quando la chat aperta e' davvero quella del
 * cliente: se il drawer era gia' aperto su un'altra conversazione, il testo
 * finirebbe nella bozza sbagliata.
 */
import type React from 'react';
import { MessageCircle } from 'lucide-react';
import { useWhatsAppChatStore } from '@/lib/stores/whatsappChatStore';
import { useWhatsAppAgentStore } from '@/lib/stores/whatsappAgentStore';
import { normalizeWhatsAppDigits } from '@/lib/services/whatsapp/phoneDigits';
import { trovaChatPerNumero } from '@/lib/services/whatsapp/chatMatch';
import { useOpenWhatsAppChat } from '@/components/whatsapp-overlay/useOpenWhatsAppChat';
import { tip } from '@/components/messages/ChatTooltip';
import { clientChatPhone, dealReminderText, type ClientPhoneLike, type DealLike } from '@/lib/utils/dealWhatsApp';

/** Oltre questo tempo senza che la chat si apra, il testo non si deposita piu'. */
const WAIT_CHAT_MS = 20_000;

function prefillWhenChatOpens(digits: string, text: string) {
  const isTarget = (id: string | null) => !!id && !!trovaChatPerNumero([{ id }], digits);
  let unsub: (() => void) | null = null;
  const timer = setTimeout(() => unsub?.(), WAIT_CHAT_MS);
  const done = () => {
    clearTimeout(timer);
    unsub?.();
    useWhatsAppAgentStore.getState().setPendingDraftText(text);
  };
  // Si aspetta sempre il cambio di chat: se il drawer era gia' su questo numero
  // la chat non cambia, quindi lo si controlla subito.
  if (isTarget(useWhatsAppChatStore.getState().selectedChatId)) { done(); return; }
  unsub = useWhatsAppChatStore.subscribe((s, prev) => {
    if (s.selectedChatId !== prev.selectedChatId && isTarget(s.selectedChatId)) done();
  });
}

interface Props {
  client: (ClientPhoneLike & { business_name?: string | null }) | null | undefined;
  /** Offerta per il testo di sollecito; senza, si apre solo la chat. */
  deal?: DealLike | null;
  className?: string;
}

export function DealWhatsAppButton({ client, deal, className }: Props) {
  const { canWhatsApp, openChat, chooser } = useOpenWhatsAppChat();
  const phone = clientChatPhone(client);
  if (!canWhatsApp || !phone) return null;

  const handleClick = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation();
    const digits = normalizeWhatsAppDigits(phone);
    if (!digits) return;
    if (deal && (deal.title || deal.quote_number)) prefillWhenChatOpens(digits, dealReminderText(deal));
    openChat(phone, e.currentTarget.getBoundingClientRect(), null, client?.business_name ?? null);
  };

  return (
    <>
      <button
        type="button"
        onClick={handleClick}
        className={className ?? 'inline-flex items-center gap-1 px-1.5 py-0.5 text-[11px] font-medium text-green-700 dark:text-green-400 hover:bg-green-50 dark:hover:bg-green-900/20 rounded transition-colors'}
        {...tip(deal ? 'Apre la chat WhatsApp del cliente con un testo di sollecito da rivedere, non inviato' : 'Apre la chat WhatsApp del cliente')}
      >
        <MessageCircle className="h-3 w-3" />
        Scrivi su WhatsApp
      </button>
      {chooser}
    </>
  );
}
